import { QuestFormSectionDivider } from './QuestFormSectionDivider';
import type { CommonQuestFormData } from './useQuestForm';

interface QuestFormFieldsProps {
    formData: CommonQuestFormData;
    questType: string;
    disableProgressType?: boolean;
    onChange: (field: string, value: string | boolean) => void;
}

export const QuestFormFields = ({
    formData,
    questType,
    disableProgressType = false,
    onChange
}: QuestFormFieldsProps) => {
    // Automatically disable "Is Optional" if quest is marked as prerequisite or primary
    const isOptionalDisabled = formData.isPrerequisite || formData.isPrimary;

    // Handler for isPrimary that also updates isOptional
    const handleIsPrimaryChange = (checked: boolean) => {
        onChange('isPrimary', checked);
        if (checked) {
            onChange('isOptional', false);
        }
    };

    // Handler for isPrerequisite that also updates isOptional
    const handleIsPrerequisiteChange = (checked: boolean) => {
        onChange('isPrerequisite', checked);
        if (checked) {
            onChange('isOptional', false);
        }
    };

    return (
        <>
            <div className='flex flex-wrap items-center gap-x-8 gap-y-2'>
                {/* Is Published */}
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="isPublished"
                        checked={formData.isPublished}
                        onChange={(e) => onChange('isPublished', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-700">
                        Is Published
                    </label>
                </div>
                {/* Is Free */}
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="isFree"
                        checked={formData.isFree}
                        onChange={(e) => onChange('isFree', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isFree" className="ml-2 block text-sm text-gray-700">
                        Is Free
                    </label>
                </div>
            </div>
            <QuestFormSectionDivider title="Quest Card details" />

            {/* Quest Type - Display Only */}
            <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                    Quest Type
                </label>
                <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700 capitalize">
                    {questType.replace('-', ' ')}
                </div>
            </div>

            <div className='flex flex-wrap items-center gap-x-8 gap-y-2'>
                {/* Is Primary */}
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="isPrimary"
                        checked={formData.isPrimary}
                        onChange={(e) => handleIsPrimaryChange(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-700">
                        Is Primary Quest
                    </label>
                </div>
                {/* Is Prerequisite */}
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="isPrerequisite"
                        checked={formData.isPrerequisite}
                        onChange={(e) => handleIsPrerequisiteChange(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isPrerequisite" className="ml-2 block text-sm text-gray-700">
                        Is Prerequisite
                    </label>
                </div>
                {/* Is Optional */}
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        id="isOptional"
                        checked={formData.isOptional}
                        onChange={(e) => onChange('isOptional', e.target.checked)}
                        disabled={isOptionalDisabled}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <label htmlFor="isOptional" className={`ml-2 block text-sm ${isOptionalDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                        Is Optional
                    </label>
                </div>
            </div>

            {/* Progress Type */}
            <div>
                <label htmlFor="progressType" className="block text-sm font-medium text-gray-700 mb-1">
                    Progress Type *
                </label>
                <select
                    id="progressType"
                    value={formData.progressType}
                    onChange={(e) => onChange('progressType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={disableProgressType}
                >
                    <option value="fixed">Fixed</option>
                    <option value="undetermined">Undetermined</option>
                </select>
            </div>

            {/* Pill Text */}
            <div>
                <label htmlFor="pillText" className="block text-sm font-medium text-gray-700 mb-1">
                    Pill Text *
                </label>
                <input
                    type="text"
                    id="pillText"
                    value={formData.pillText}
                    onChange={(e) => onChange('pillText', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Chat with Ollie"
                    required
                />
            </div>

            {/* Text Templating Prompt */}
            <div>
                <label htmlFor="textTemplatingPrompt" className="block text-sm font-medium text-gray-700 mb-1">
                    Text Templating Prompt *
                </label>
                <textarea
                    id="textTemplatingPrompt"
                    value={formData.textTemplatingPrompt}
                    onChange={(e) => onChange('textTemplatingPrompt', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter templating prompt"
                    rows={4}
                    required
                />
            </div>
        </>
    );
};
